import dayjs from 'dayjs'

import { userManager } from '@/Core/Managers'

import { LoadCurrencyTask } from './LoadCurrencyTask'

import type { ApplicationFormContextLike } from '../Contracts/IApplication'

const isBlank = (value: unknown): boolean =>
    value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

/** Валюти вартостей у формі — UAH (легасі `ApplicationResource.jsx:152-157`, ЗА-08). */
export const APPLICATION_COST_CURRENCY_CODE = 'UAH'

/** Пари «вартість + валюта» картки заявки. */
export const APPLICATION_COST_CURRENCY_FIELDS = ['costClientCurrency', 'costCarrierCurrency', 'costToBorderCurrency'] as const

/** «Термін оплати» за замовчуванням (легасі `ApplicationResource.jsx:158-162`). */
const DEFAULT_PAYMENT_TERM = 10

/** Поточний користувач — значенням select-а (ядро впізнає вибране за `@id`). */
export const currentUserRef = (): Record<string, unknown> | null => {
    const user = userManager.user as Record<string, unknown>
    const uuid = typeof user['uuid'] === 'string' ? user['uuid'] : null

    if (!uuid) return null

    return { ...user, '@id': typeof user['@id'] === 'string' ? user['@id'] : `/services/users/${uuid}`, uuid }
}

/**
 * Значення форми створення заявки (`SPEC-021` §4.1):
 *
 *  - **«Менеджер клієнт» — той, хто створює** (ЗА-05, легасі `ApplicationCrud.jsx` `initialValues.manager`);
 *  - **валюти трьох вартостей — UAH** (ЗА-08);
 *  - **«Термін оплати» — 10**;
 *  - **«Дата доставки на митницю» — сьогодні 08:00 на момент відкриття форми**
 *    (легасі ставив час завантаження модуля, ДЗ-09).
 *
 * Пише лише в порожні поля й через `setInitialValue`: значення з копії,
 * конвертації чи вкладки CRM не перезаписується, а дефолт не рахується
 * незбереженою зміною.
 */
export const ApplyApplicationCreateDefaultsTask = async (formContext: ApplicationFormContextLike): Promise<void> => {
    const values = formContext.values ?? {}

    if (isBlank(values['manager'])) {
        const user = currentUserRef()

        if (user) formContext.setInitialValue?.('manager', user)
    }

    if (isBlank(values['paymentTerm'])) formContext.setInitialValue?.('paymentTerm', DEFAULT_PAYMENT_TERM)

    if (isBlank(values['dateDeliveryCustoms'])) {
        formContext.setInitialValue?.('dateDeliveryCustoms', dayjs().hour(8).minute(0).second(0).millisecond(0).format())
    }

    if (APPLICATION_COST_CURRENCY_FIELDS.every((field) => !isBlank(formContext.values?.[field]))) return

    const currency = await LoadCurrencyTask(APPLICATION_COST_CURRENCY_CODE)

    if (!currency) return

    for (const field of APPLICATION_COST_CURRENCY_FIELDS) {
        if (isBlank(formContext.values?.[field])) formContext.setInitialValue?.(field, currency)
    }
}
